/**
 * Session state for the SPA — plan §9.0.
 *
 * The browser holds nothing but an HttpOnly cookie it cannot read. Identity is
 * resolved by asking the Worker, which is also what makes revocation immediate:
 * deleting the `sessions` row ends the session on the next request.
 */

import { useCallback, useEffect, useState } from 'react';
import { clearApiCache } from './api';

export interface SessionUser {
  id: number;
  email: string;
  fullName: string | null;
  role: 'user' | 'manager' | 'admin';
  timezone: string;
}

export type SessionState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: SessionUser };

export function useSession() {
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/auth/me', { credentials: 'same-origin' });
      if (!response.ok) {
        setState({ status: 'anonymous' });
        return;
      }
      const body = (await response.json()) as { user: SessionUser };
      setState({ status: 'authenticated', user: body.user });
    } catch {
      // A network failure is not proof of being signed out, but treating it as
      // anonymous is the safe direction — the API re-checks every request
      // anyway, so the worst case is one unnecessary sign-in.
      setState({ status: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(() => {
    const returnTo = window.location.pathname + window.location.search;
    window.location.href = `/auth/login?return_to=${encodeURIComponent(returnTo)}`;
  }, []);

  const signOut = useCallback(async () => {
    await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    /*
     * Drop the conditional-request cache with the session.
     *
     * Not strictly required — an ETag is scoped to the caller (see lib/etag.ts),
     * so the server would refuse to 304 one person's body to another and the
     * next request simply returns 200. But leaving one user's responses in
     * memory on a shared machine because the safety is two files away is the
     * kind of thing that is correct until someone changes the other file.
     */
    clearApiCache();
    setState({ status: 'anonymous' });
  }, []);

  return { state, signIn, signOut, refresh };
}
